import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Search, Edit, Trash2, Save, X, Loader2,
  BarChart3, Tag, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

/**
 * Admin Knowledge Base Management
 * Manage AI knowledge articles
 */
export default function AdminKnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    tags: '',
    jurisdiction: ''
  });

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'rent_arrears', label: 'Rent & Arrears' },
    { value: 'tenancy', label: 'Tenancy' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'deposits', label: 'Deposits' },
    { value: 'legal', label: 'Legal' },
    { value: 'safety', label: 'Safety' },
  ];

  useEffect(() => {
    loadArticles();
    loadStats();
  }, [filterCategory, searchTerm]);

  const loadArticles = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const { data } = await api.get(`/knowledge-base?${params}`);
      setArticles(data);
    } catch (error) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/knowledge-base/stats');
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const saveArticle = async () => {
    if (!formData.title || !formData.content || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        category: formData.category,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        jurisdiction: formData.jurisdiction || null
      };

      if (editingArticle) {
        await api.put(`/knowledge-base/${editingArticle.id}`, payload);
        toast.success('Article updated');
      } else {
        await api.post('/knowledge-base', payload);
        toast.success('Article created');
      }

      setShowNew(false);
      setEditingArticle(null);
      setFormData({ title: '', content: '', category: 'general', tags: '', jurisdiction: '' });
      loadArticles();
      loadStats();
    } catch (error) {
      toast.error('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      await api.delete(`/knowledge-base/${id}`);
      toast.success('Article deleted');
      loadArticles();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const startEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      category: article.category,
      tags: article.tags?.join(', ') || '',
      jurisdiction: article.jurisdiction || ''
    });
    setShowNew(true);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl pb-20 lg:pb-6">
        {/* Header - mobile responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-display font-bold text-slate-900">Knowledge Base</h1>
              <p className="text-sm text-slate-600">Manage AI knowledge for Denise</p>
            </div>
          </div>
          <Button onClick={() => { setShowNew(true); setEditingArticle(null); setFormData({ title: '', content: '', category: 'general', tags: '', jurisdiction: '' }); }} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Add Article
          </Button>
        </div>

        {/* Stats - mobile responsive grid */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-slate-500">Total Articles</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.total_articles}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-slate-500">Categories</p>
                <p className="text-xl sm:text-2xl font-bold">{Object.keys(stats.by_category || {}).length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-slate-500">Most Used</p>
                <p className="text-xs sm:text-sm font-medium truncate">{stats.most_used?.[0]?.title || 'N/A'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-slate-500">Most Helpful</p>
                <p className="text-xs sm:text-sm font-medium truncate">{stats.most_helpful?.[0]?.title || 'N/A'}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* New/Edit Form */}
        {showNew && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base sm:text-lg">{editingArticle ? 'Edit Article' : 'New Article'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowNew(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(f => ({...f, title: e.target.value}))}
                      placeholder="Article title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(f => ({...f, category: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                      {categories.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Content *</label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData(f => ({...f, content: e.target.value}))}
                    placeholder="Article content - this is what Denise will use to answer questions"
                    rows={6}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
                    <Input
                      value={formData.tags}
                      onChange={(e) => setFormData(f => ({...f, tags: e.target.value}))}
                      placeholder="e.g., boiler, heating, emergency"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Jurisdiction</label>
                    <select
                      value={formData.jurisdiction}
                      onChange={(e) => setFormData(f => ({...f, jurisdiction: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                      <option value="">All UK</option>
                      <option value="england">England</option>
                      <option value="scotland">Scotland</option>
                      <option value="wales">Wales</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={saveArticle} disabled={saving} className="w-full sm:w-auto">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    {editingArticle ? 'Update' : 'Create'} Article
                  </Button>
                  <Button variant="outline" onClick={() => setShowNew(false)} className="w-full sm:w-auto">Cancel</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters - mobile responsive */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4">
          <div className="flex-1">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search articles..."
              className="w-full"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Articles List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : articles.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No articles found</p>
              </div>
            ) : (
              <div className="divide-y">
                {articles.map((article) => (
                  <div key={article.id} className="p-3 sm:p-4 hover:bg-slate-50">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900 text-sm sm:text-base">{article.title}</h3>
                        <p className="text-xs sm:text-sm text-slate-500 mt-1 line-clamp-2">{article.content}</p>
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{article.category}</span>
                          {article.tags?.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded">{tag}</span>
                          ))}
                          <span className="text-xs text-slate-400">Used: {article.usage_count || 0}</span>
                        </div>
                      </div>
                      <div className="flex gap-1 sm:gap-2 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(article)} className="h-8 w-8 p-0">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteArticle(article.id)} className="h-8 w-8 p-0">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
